import React from 'react'

const Seat = ({ id, status, isSelected, onClick }) => {
    const getStatusColor = () => {
        if (isSelected) return 'bg-[#AC5E2A] text-white border-[#AC5E2A]'
        if (status === 'occupied') return 'bg-red-500 text-white border-red-500'
        return 'bg-white text-gray-800 border-gray-200'
    }

    return (
        <button
            onClick={() => status !== 'occupied' && onClick(id)}
            disabled={status === 'occupied'}
            className={`w-6 h-6 border flex items-center justify-center text-[10px] font-bold rounded shadow-sm transition-all active:scale-90 ${getStatusColor()} ${status === 'occupied' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-[#AC5E2A] font-bold'}`}
        >
            {id}
        </button>
    )
}

const SeatBlock = ({ seats, data, onSeatClick, selectedSeat, isColumn }) => {
    return (
        <div className={`flex flex-wrap gap-1 justify-center items-center p-1 bg-gray-50 rounded-lg border border-gray-100 ${isColumn ? 'flex-col' : ''}`}>
            {data.map((row, rIdx) => (
                <div key={rIdx} className='flex gap-1'>
                    {row.map((id) => (
                        <Seat
                            key={id}
                            id={id}
                            status={seats[id]?.status}
                            isSelected={selectedSeat === id}
                            onClick={onSeatClick}
                        />
                    ))}
                </div>
            ))}
        </div>
    )
}

const formatTarih = (iso) => {
    if (!iso) return ''
    try {
        const [y, m, d] = iso.split('-')
        return `${d}.${m}.${y}`
    } catch {
        return iso
    }
}

const LockedHall = ({ hallId, sebep, duyuru, tarih }) => (
    <div className='flex flex-col items-center justify-center p-8 min-h-[300px] text-center animate-fadeIn bg-white/50 backdrop-blur-sm rounded-2xl border border-gray-100 shadow-sm'>
        <div className='w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center mb-5 shadow-inner'>
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-red-400">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
        </div>
        <h3 className='text-lg font-bold text-gray-800 mb-2'>{hallId}. Salon Kapalı</h3>
        {sebep && <p className='text-xs font-bold text-red-500 uppercase tracking-wider mb-2'>{sebep}</p>}
        {tarih && <p className='text-xs text-amber-700 bg-amber-50 px-3 py-1 rounded-full inline-block mb-3 font-bold'>📅 {formatTarih(tarih)}</p>}
        <p className='text-gray-500 text-sm leading-relaxed max-w-md'>
            {duyuru || 'Bu salon belirtilen tarihte hizmet verememektedir.'}
        </p>
    </div>
)

const SeatLayout = ({ hallId, seats, selectedSeat, onSeatClick, lockedHalls = [] }) => {
    // lockedHalls eski format ([1, 2]) veya yeni format ([{hall_id, tarih, sebep, duyuru}]) olabilir
    const lockedInfo = lockedHalls
        .map((lh) => (typeof lh === 'object' ? lh : { hall_id: lh }))
        .find((lh) => lh.hall_id === hallId)

    if (lockedInfo) {
        return <LockedHall hallId={hallId} sebep={lockedInfo.sebep} duyuru={lockedInfo.duyuru} tarih={lockedInfo.tarih} />
    }

    if (hallId === 1) {
        return (
            <div className='space-y-6 animate-fadeIn'>
                {/* 1. Salon artık sadece 1-40 arası blokları içeriyor */}
                <div className='grid grid-cols-3 gap-1'>
                    {/* Block 1: 40-35 */}
                    <SeatBlock seats={seats} data={[[40, 39, 38], [37, 36, 35]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} />
                    {/* Block 2: 28-23 */}
                    <SeatBlock seats={seats} data={[[28, 27, 26], [25, 24, 23]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} />
                    {/* Block 3: 16-9 */}
                    <SeatBlock seats={seats} data={[[16, 15, 14, 13], [12, 11, 10, 9]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} />
                    {/* Block 4: 34-29 */}
                    <SeatBlock seats={seats} data={[[34, 33, 32], [31, 30, 29]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} />
                    {/* Block 5: 22-17 */}
                    <SeatBlock seats={seats} data={[[22, 21, 20], [19, 18, 17]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} />
                    {/* Block 6: 8-1 */}
                    <SeatBlock seats={seats} data={[[8, 7, 6, 5], [4, 3, 2, 1]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} />
                </div>
            </div>
        )
    }

    // 1. Salon doluluk kontrolü
    const isHall1Full = React.useMemo(() => {
        if (!seats) return false
        // 1. Salon koltukları: 1-40
        for (let i = 1; i <= 40; i++) {
            // Eğer herhangi bir koltuk dolu değilse (undefined veya occupied değilse), salon 1 dolmamış demektir
            if (seats[i]?.status !== 'occupied') {
                return false
            }
        }
        return true
    }, [seats])

    if (!isHall1Full) {
        return (
            <div className='flex flex-col items-center justify-center p-8 min-h-[400px] text-center animate-fadeIn bg-white/50 backdrop-blur-sm rounded-2xl border border-gray-100 shadow-sm'>
                <div className='w-24 h-24 bg-[#AC5E2A]/5 rounded-2xl flex items-center justify-center mb-6 shadow-inner animate-pulse-slow'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-[#AC5E2A]/40">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                </div>
                <h3 className='text-xl font-bold text-gray-800 mb-3'>Salon 2 Henüz Açılmadı</h3>
                <p className='text-gray-500 max-w-md mx-auto leading-relaxed mb-6'>
                    1. Salon tamamen dolmadan 2. Salon kullanıma açılmamaktadır.
                </p>
                <div className='inline-flex items-center gap-2 px-4 py-2 bg-[#AC5E2A]/10 text-[#AC5E2A] rounded-full text-sm font-medium border border-[#AC5E2A]/20'>
                    <span>Lütfen 1. Salondaki boş yerleri tercih ediniz.</span>
                </div>
            </div>
        )
    }

    return (
        <div className='space-y-1 animate-fadeIn'>
            {/* Hall 2 Bloklar (41-70) */}
            <div className='grid grid-cols-2 gap-1'>
                <SeatBlock seats={seats} data={[[52, 51, 50]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} />
                <SeatBlock seats={seats} data={[[49, 48, 47]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} />
            </div>

            <div className='grid grid-cols-2 gap-1'>
                <div className='space-y-1'>
                    <SeatBlock seats={seats} data={[[55, 54, 53], [58, 57, 56]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} isColumn />
                    <SeatBlock seats={seats} data={[[61, 60, 59], [64, 63, 62]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} isColumn />
                    <SeatBlock seats={seats} data={[[68, 65], [69, 66], [70, 67]]} onSeatClick={onSeatClick} selectedSeat={selectedSeat} isColumn />
                </div>

                <div className='flex flex-col gap-1'>
                    <div className='bg-gray-50 p-1 rounded-lg border border-gray-100 flex flex-col gap-2 w-max ml-auto'>
                        <div className='flex gap-1'>
                            <Seat id={46} status={seats[46]?.status} isSelected={selectedSeat === 46} onClick={onSeatClick} />
                            <Seat id={43} status={seats[43]?.status} isSelected={selectedSeat === 43} onClick={onSeatClick} />
                        </div>
                        <div className='flex gap-1'>
                            <Seat id={45} status={seats[45]?.status} isSelected={selectedSeat === 45} onClick={onSeatClick} />
                            <Seat id={42} status={seats[42]?.status} isSelected={selectedSeat === 42} onClick={onSeatClick} />
                        </div>
                        <div className='flex gap-1'>
                            <Seat id={44} status={seats[44]?.status} isSelected={selectedSeat === 44} onClick={onSeatClick} />
                            <Seat id={41} status={seats[41]?.status} isSelected={selectedSeat === 41} onClick={onSeatClick} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SeatLayout
